import { useState, useCallback } from 'react';
import { v4 as uuid } from 'uuid';
import type { DocNode } from '@atlaskit/adf-schema';
import { performPostRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useIssueKey, useIssueId } from '@atlassian/jira-issue-context-service/src/main.tsx';
import { useFieldValue } from '@atlassian/jira-issue-field-base/src/services/field-value-service/index.tsx';
import { useProjectId, useProjectKey } from '@atlassian/jira-project-context-service/src/main.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	DescriptionKey,
	SummaryKey,
} from '@atlassian/jira-providers-issue/src/model/issue-system-fields.tsx';
import { convertAdfToText } from '@atlassian/jira-rich-content/src/common/convert-adf-to-text/index.tsx';
import { useIssueTypeName } from '@atlassian/jira-issue-context-service/src/context.tsx';
import type { LabelOption } from '@atlassian/jira-issue-shared-types/src/common/types/labels-type.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	FIELD_SUGGESTIONS_SOURCE,
	getFieldSuggestionAiAttributes,
} from '../../common/get-ai-attributes.tsx';

type AISuggestedLabelData = {
	// `LabelOption` is the data type directly used by the labels components
	data: LabelOption[] | undefined;
	error: unknown | undefined;
	loading: boolean;
	completed: boolean;
};

type AISuggestedLabelApiResponse = {
	ai_feature_output: {
		suggested_issues: Array<{
			field_values: {
				Labels: string[];
			};
		}>;
	};
};

const FIELD_SUGGESTION_API =
	'/gateway/api/assist/ai/v2/ai-feature/jira/issue/source-type/work-item/suggestions';
const DEFAULT_MAX_SUGGESTIONS = 5;
const DEFAULT_ISSUE_TYPE_NAME = 'Task';
const DEFAULT_LOCALE = 'en-US';
const DEFAULT_SOURCE = 'JIRA';
const DEFAULT_MAX_ISSUES = 1;

export const parseSuggestedLabelResponse = (
	response: AISuggestedLabelApiResponse,
): LabelOption[] => {
	const labels = response?.ai_feature_output?.suggested_issues?.[0]?.field_values?.Labels || [];
	return labels.map((label, index) => ({
		id: index,
		label,
		value: label,
		isAiSuggested: true,
	}));
};

export const createSuggestedLabelPayload = (
	summary: string,
	issueType: string,
	projectId?: string,
	description?: DocNode,
	issueKey?: string,
	issueId?: string,
) => ({
	ai_feature_input: {
		source: DEFAULT_SOURCE,
		locale: DEFAULT_LOCALE,
		context: {
			summary,
			...(projectId && { project_id: projectId }),
			...(issueKey && { issue_key: issueKey }),
			...(issueId && { issue_id: issueId }),
			...(description && { description: convertAdfToText(description) }),
			use_similar_issues: expVal('jira_ai_label_field_suggestions', 'useSimilarIssues', false),
			variant: expVal('jira_ai_label_field_suggestions', 'variant', 'control'),
		},
		suggested_issues_config: {
			max_issues: DEFAULT_MAX_ISSUES,
			suggested_issue_field_types: [
				{
					issue_type: issueType,
					fields: [
						{
							field_name: 'Labels',
							field_type: 'Short-Text-List',
							max_field_suggestions: DEFAULT_MAX_SUGGESTIONS,
						},
					],
				},
			],
		},
	},
});

/**
 * Hook to fetch AI-suggested labels for a Jira issue.
 *
 * @returns {Object} Contains:
 * - `data`: Suggested labels or `undefined`.
 * - `error`: Fetch error or `undefined`.
 * - `loading`: `true` if fetching, otherwise `false`.
 * - `completed`: `true` if fetching is completed, otherwise `false`.
 * - `fetchSuggestion`: Function to trigger fetching labels.
 */
export const useFetchAiSuggestedLabels = () => {
	const issueKey = useIssueKey();
	const projectKey = useProjectKey(issueKey);
	const projectId = useProjectId(projectKey);
	const issueId = useIssueId();
	const [issueTypeName = DEFAULT_ISSUE_TYPE_NAME] = useIssueTypeName();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [descriptionValue] = useFieldValue({ issueKey, fieldKey: DescriptionKey });
	const [summaryValue] = useFieldValue({ issueKey, fieldKey: SummaryKey });

	const [state, setState] = useState<AISuggestedLabelData>({
		data: undefined,
		error: undefined,
		loading: false,
		completed: false,
	});

	const fetchAiLabelSuggestions = useCallback(async () => {
		const payload = createSuggestedLabelPayload(
			summaryValue,
			issueTypeName,
			projectId,
			descriptionValue,
			issueKey,
			issueId,
		);

		setState({
			error: undefined,
			data: undefined,
			loading: true,
			completed: false,
		});
		try {
			const result = await performPostRequest(FIELD_SUGGESTION_API, {
				headers: {
					'Content-Type': 'application/json',
					'X-Product': 'jira',
					'X-Experience-Id': 'jira-field-suggestions',
					'X-Channel-Id': 'jira-field-suggestions',
				},
				body: JSON.stringify(payload),
			});

			setState({
				data: parseSuggestedLabelResponse(result),
				error: undefined,
				loading: false,
				completed: true,
			});

			return result;
		} catch (error: unknown) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'aiResult error', {
				...getFieldSuggestionAiAttributes(uuid(), FIELD_SUGGESTIONS_SOURCE.API_CALL),
				error: error instanceof Error ? error.message : 'Unknown error',
			});

			setState({
				data: undefined,
				error,
				loading: false,
				completed: true,
			});
			return undefined;
		}
	}, [
		descriptionValue,
		issueKey,
		issueId,
		projectId,
		summaryValue,
		issueTypeName,
		createAnalyticsEvent,
	]);

	return {
		...state,
		fetchAiLabelSuggestions,
	};
};
